import React, {
  useLayoutEffect,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import * as d3 from "d3";
import {
  loadPrecomputedCourseData,
  applyPCA,
  applyTSNE,
  useLoadData,
} from "./CourseSimilarityPrecomputedProcessor";
import { CURRENT_SEMESTER, AVAILABLE_SEMESTERS, getSemesterDataPaths, API_BASE_URL } from "./config";
import CoursePopup from "./CoursePopup";
import { supabase } from "./supabaseClient"; // make sure this points to your initialized Supabase client
// import { API_BASE_URL } from './config';
import Settings from './Settings';
import html2canvas from 'html2canvas'; // Import html2canvas
// OnboardingPopup replaced by the SiteTour (live spotlight) rendered at App level

// Using global context
import { useSemester } from './SemesterContext';
import domtoimage from 'dom-to-image-more';



// Use the globally defined current semester
// const semester = CURRENT_SEMESTER;
const backendUrl = API_BASE_URL;

// === Tranche & Shape Definitions ===
const TRANCHE_SHAPES = {
  "Arts": "circle",
  "Humanities": "square",
  "Sciences": "triangle",
  "Social Sciences": "star",
  "First Year Seminar": "doubleCircle"
};

// Helper function to normalize department codes
const normalizeDepartment = (dept) => {
  if (dept === "WAGS") return "SWAG";
  return dept;
};

const TRANCHES = {
  "Arts": ["ARCH", "ARHA", "MUSI", "MUSL", "THDA"],
  "Humanities": [
    "AAPI",
    "AMST",
    "ARAB",
    "ASLC",
    "BLST",
    "CHIN",
    "CLAS",
    "COLQ",
    "EDST",
    "ENGL",
    "ENST",
    "EUST",
    "FAMS",
    "FREN",
    "GERM",
    "GREE",
    "HIST",
    "JAPA",
    "LATI",
    "LJST",
    "LLAS",
    "PHIL",
    "RELI",
    "RUSS",
    "SPAN",
    "SWAG",
  ],
  "Sciences": [
    "ASTR",
    "BCBP",
    "BIOL",
    "CHEM",
    "COSC",
    "GEOL",
    "MATH",
    "NEUR",
    "PHYS",
    "STAT",
  ],
  "Social Sciences": ["ANTH", "ECON", "POSC", "PSYC", "SOCI"],
  "First Year Seminar": ["FYSE"],
};

const getTrancheForDept = (dept) => {
  const normalizedDept = normalizeDepartment(dept);
  for (const [tranche, majors] of Object.entries(TRANCHES)) {
    if (majors.includes(normalizedDept)) return tranche;
  }
  return "other";
};

const getShapeForDept = (dept) =>
  TRANCHE_SHAPES[getTrancheForDept(dept)] || "circle";

export default function CourseSimilarityPrecomputedGraph({
  mode,
  highlighted,
  conflicted,
  onSemesterChange,
  onHighlight,
  onConflicted,
  isPublicMode = false,
  showOnboarding,
  setShowOnboarding,
  captureRef
}) {
  const svgRef = useRef(null);
  const [svgReady, setSvgReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [courseDetailsData, setCourseDetailsData] = useState(null);
  const [tsneCoords, setTsneCoords] = useState(null);
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [selectedCourse, setSelectedCourse] = useState(null);
  // In your component:
  const { selectedSemester, setSelectedSemester } = useSemester(); // Use global state
  const [userId, setUserId] = useState(null);
  const [activeTab, setActiveTab] = useState(isPublicMode ? 'thisSemester' : 'thisSemester');
  const [showConflicts, setShowConflicts] = useState(true);
  const mapContainerRef = useRef(null);
  const contentToCaptureRef = useRef(null);
  const [legendCollapsed, setLegendCollapsed] = useState(
    !new URLSearchParams(window.location.search).has("legend")
  );


  // Add a new state variable to hold backend output data
  const [backendOutputData, setBackendOutputData] = useState(null);

  // Add new state for history data
  const [historyData, setHistoryData] = useState(null);
  const [userCourseCodes, setUserCourseCodes] = useState([]);

  const [showSettings, setShowSettings] = useState(false);
  const [tooltip, setTooltip] = useState(null); // { x, y, code, title }

  // Check if this is the user's first visit
  useEffect(() => {
    // ?skipTour bypass (used by preview/screenshot tooling)
    if (new URLSearchParams(window.location.search).has('skipTour')) return;
    const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
    if (!hasSeenOnboarding) {
      setShowOnboarding(true);
    }
  }, []);

  // Log dimensions when they change
  useEffect(() => {
    //console.log('Current dimensions:', dimensions);
  }, [dimensions]);

  // Combined effect to handle semester changes and tab switches
  useEffect(() => {
    onSemesterChange?.(selectedSemester);

    // Only populate user courses if we're in Single Semester View and not in public mode
    if (activeTab === 'thisSemester' && !isPublicMode && userCourseCodes && userCourseCodes.length > 0) {
      console.log('Tab/UserCourses effect triggered:', { activeTab, selectedSemester, userCourseCodesLength: userCourseCodes?.length, isPublicMode });

      const userCoursesForSemester = userCourseCodes
        .filter(uc => uc.semester === selectedSemester)
        .map(uc => uc.code);

      console.log(`Populating user courses for semester ${selectedSemester}:`, userCoursesForSemester);

      if (userCoursesForSemester.length > 0) {
        onHighlight(userCoursesForSemester);
      } else {
        // Clear highlights if no courses for this semester
        onHighlight([]);
      }
    }
  }, [selectedSemester, activeTab, userCourseCodes, isPublicMode, onSemesterChange, onHighlight]);

  const setSvgRef = useCallback((node) => {
    svgRef.current = node;
    if (node) setSvgReady(true);
  }, []);

  //console.log("Conflicted array:", conflicted);


  useEffect(() => {
    if (!mapContainerRef.current) return;

    let timeoutId;
    const resizeObserver = new ResizeObserver((entries) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const { width, height } = entries[0].contentRect;
        setDimensions({ width, height });
      }, 16); // One frame delay
    });

    resizeObserver.observe(mapContainerRef.current);
    if (captureRef) {
      captureRef.current = mapContainerRef.current;
    }
    return () => {
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
    };
  }, []);

  // Effect to extract user course codes whenever backend data is available
  useEffect(() => {
    if (backendOutputData && !isPublicMode) {
      console.log("Extracting user course codes from backend data:", backendOutputData);

      // Get the user's course codes with their semesters for highlighting
      const userCourses = backendOutputData
        .filter(course => course && course.course_code)
        .map(course => ({
          code: course.course_code,
          semester: course.semester
        }));
      console.log("User course codes extracted:", userCourses);
      setUserCourseCodes(userCourses);
    }
  }, [backendOutputData, isPublicMode]);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);

        // In public mode, force single semester view
        if (isPublicMode) {
          setActiveTab('thisSemester');
        }

        if (activeTab === 'thisSemester') {
          // Load single semester data
          const { courses, courseDetails, tsneCoords: filteredTsneCoords } = await loadPrecomputedCourseData(selectedSemester);
          setGraphData({ courses, similarityMatrix: null });
          setCourseDetailsData(courseDetails);
          setTsneCoords(filteredTsneCoords);
        } else {
          // Load history data (only if not in public mode)
          if (backendOutputData && !isPublicMode) {
            console.log("Processing backend data for history:", backendOutputData);

            // Get unique semesters from the history data
            const historySemesters = [...new Set(backendOutputData.map(course => course.semester))];
            console.log("Found history semesters:", historySemesters);

            // Group courses by semester for the history display
            const coursesBySemester = backendOutputData.reduce((acc, course) => {
              if (!course || !course.course_code) return acc;
              const semester = course.semester;
              if (!acc[semester]) {
                acc[semester] = [];
              }
              acc[semester].push({
                code: course.course_code,
                department: course.course_code.split('-')[0],
              });
              return acc;
            }, {});
            console.log("Grouped courses by semester:", coursesBySemester);

            // Identify semesters where the user took an FYSE course
            const userFyseSemesters = new Set(userCourseCodes
              .filter(course => course.code.startsWith('FYSE-'))
              .map(course => course.semester)
            );
            console.log("User FYSE semesters:", Array.from(userFyseSemesters));

            // Load all courses and coordinates for all semesters in history
            const allCourses = [];
            const allCourseDetailsMap = new Map();
            const allTsneCoords = [];

            for (const semester of historySemesters) {
              const { courses, courseDetails, tsneCoords } = await loadPrecomputedCourseData(semester);
              console.log(`Data for semester ${semester}:`, { courses, courseDetails, tsneCoords });

              // Filter FYSE courses if the user did not take an FYSE course in this semester
              const shouldIncludeFyse = userFyseSemesters.has(semester);

              // Add semester information to each course and apply FYSE filter
              const coursesWithSemester = courses
                .filter(course => shouldIncludeFyse || !course.code.startsWith('FYSE-'))
                .map(course => ({
                  ...course,
                  semester
                }));
              allCourses.push(...coursesWithSemester);

              // Accumulate unique course details using a Map for robustness
              courseDetails.forEach(cd => {
                if (cd.course_codes && Array.isArray(cd.course_codes)) {
                  cd.course_codes.forEach(code => {
                    // Apply FYSE filter to courseDetails as well
                    if (shouldIncludeFyse || !code.startsWith('FYSE-')) {
                      allCourseDetailsMap.set(code, cd);
                    }
                  });
                }
              });

              //BUG: Issue with 0910F ARHA-37 - caused history to crash: why?
              // Add semester information to each tsneCoord and apply FYSE filter
              const tsneCoordsWithSemester = tsneCoords
                .filter(coord => shouldIncludeFyse || !coord.codes.some(code => code.startsWith('FYSE-')))
                .map(coord => ({
                  ...coord,
                  semester
                }));
              allTsneCoords.push(...tsneCoordsWithSemester);
            }

            console.log("Final processed data:", {
              allCourses,
              courseDetails: Array.from(allCourseDetailsMap.values()),
              tsneCoords: allTsneCoords
            });

            setGraphData({ courses: allCourses, similarityMatrix: null });
            setCourseDetailsData(Array.from(allCourseDetailsMap.values()));
            setTsneCoords(allTsneCoords);

            // Store the semester information for display
            setHistoryData(coursesBySemester);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error loading data:", err);
        setError(err.message);
        setLoading(false);
      }
    }

    loadData();
  }, [selectedSemester, activeTab, backendOutputData, isPublicMode, userCourseCodes]);

  useEffect(() => {
    async function fetchUser() {
      // Skip authentication in public mode
      if (isPublicMode) {
        console.log("Public mode - skipping authentication");
        return;
      }

      const {
        data: { user },
        error,
      } = await supabase.auth.getUser();

      if (error) {
        console.error("Error fetching user:", error);
      } else if (user) {
        setUserId(user.id);
      } else {
        console.warn("No user logged in.");
      }
    }
    fetchUser();
  }, [isPublicMode]);


  async function fetchBackendData() {

    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
      console.error("No valid session token found");
      return;
    }

    // Skip backend calls in public mode
    if (isPublicMode) {
      console.log("Public mode - skipping backend data fetch");
      setBackendOutputData([]);
      return;
    }

    try {
      //console.log("Using backend URL:", backendUrl); // Add this for debugging!
      const response = await fetch(`${backendUrl}/retrieve_courses`, { // await fetch(`${API_BASE_URL}/retrieve_courses`
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({}) // no user_id
      });

      if (!response.ok) {
        throw new Error(`Backend error: ${response.status}`);
      }

      const data = await response.json();
      console.log("Backend response data:", data); // <-- log the response here
      setBackendOutputData(data);  // store output here
    } catch (err) {
      setError(err.message);
      console.error("Error fetching backend data:", err);
    }
  }

  useEffect(() => {
    if (isPublicMode) {
      // In public mode, set empty backend data immediately
      setBackendOutputData([]);
      return;
    }

    if (!userId) return;
    fetchBackendData();
  }, [userId, isPublicMode]);

  // Add a function to check if there's any history data
  const hasHistoryData = () => {
    console.log("Checking history data:", backendOutputData); // <-- log the data check
    return backendOutputData && backendOutputData.length > 0;
  };

  // Add a function to get semesters with data
  const getSemestersWithData = () => {
    console.log("Getting semesters with data:", historyData); // <-- log the history data
    if (!historyData) return [];
    return Object.keys(historyData).sort();
  };

  useLayoutEffect(() => {
    // Ensure SVG is ready and all necessary data is loaded and in expected format
    if (!svgReady || !graphData || !courseDetailsData || !tsneCoords ||
      !Array.isArray(tsneCoords) || tsneCoords.length === 0 ||
      !Array.isArray(courseDetailsData) || courseDetailsData.length === 0) {
      return;
    }

    try {
      const { courses } = graphData;
      const courseDetails = courseDetailsData;
      const precomputedTSNECoords = tsneCoords;
      let coordinates;

      if (mode === "pca") {
        // coordinates = applyPCA(similarityMatrix);
      } else {
        const codeToIndex = new Map(courses.map((c, i) => [c.code, i]));
        coordinates = Array(courses.length)
          .fill()
          .map(() => [NaN, NaN]);
        precomputedTSNECoords.forEach(({ codes: tsneCoordCodes, x, y }) => {
          const currentTsneCodes = Array.isArray(tsneCoordCodes) ? tsneCoordCodes : [tsneCoordCodes]; // Ensure 'codes' is always an array
          currentTsneCodes.forEach(code => {
            const idx = codeToIndex.get(code);
            if (idx !== undefined) {
              coordinates[idx] = [x, y];
            }
          });
        });
      }

      // Restrained per-division hue families (Amherst-aligned, calm/cartographic):
      // each academic division owns one hue; departments within it vary only in
      // lightness so a division reads as a coherent colored territory rather than
      // a clash of rainbow hues.
      // Vibrant data-viz palette (chosen for legibility, not brand). Each
      // division owns a HUE BAND; departments are spread across the band and
      // alternate light/dark + saturation so every department is clearly
      // distinguishable, while the band still keeps a division loosely grouped.
      // Soft PASTEL palette. Each division owns a hue band; departments spread
      // across it (close clusters → related hues) with gentle light/dark
      // alternation so they're still distinguishable. Pastel = high lightness,
      // moderate saturation → calm, not clashing.
      const TRANCHE_BAND = {
        "Arts":            { h0: 12,  h1: 42,  s: 0.55, l: 0.70 }, // peach → apricot
        "Sciences":        { h0: 105, h1: 162, s: 0.42, l: 0.68 }, // mint → sage
        "Humanities":      { h0: 193, h1: 228, s: 0.48, l: 0.71 }, // powder blue → periwinkle
        "Social Sciences": { h0: 272, h1: 322, s: 0.42, l: 0.74 }, // lavender → lilac/pink
      };
      const clamp01 = (v) => Math.max(0, Math.min(1, v));
      const majorColorMap = new Map();
      for (const [tranche, majors] of Object.entries(TRANCHES)) {
        const realDepts = majors.filter(m => m !== "FYSE");
        const band = TRANCHE_BAND[tranche] || { h0: 272, h1: 322, s: 0.42, l: 0.74 };
        const n = realDepts.length;
        realDepts.forEach((major, i) => {
          if (majorColorMap.has(major)) return;
          const t = n > 1 ? i / (n - 1) : 0.5;
          const hue = band.h0 + t * (band.h1 - band.h0);
          const sat = clamp01(band.s + (i % 2 ? 0.08 : -0.05));
          const lit = clamp01(band.l + (i % 2 ? -0.07 : 0.05));
          majorColorMap.set(major, d3.hsl(hue, sat, lit).toString());
        });
      }
      majorColorMap.set("FYSE", "#c9c6cf"); // pale neutral gray

      // Merge nodes with the same coordinates
      const mergedNodes = new Map();
      precomputedTSNECoords.forEach(({ codes: tsneCoordCodes, x, y, semester: coordSemester, isReference }) => {
        const currentTsneCodes = Array.isArray(tsneCoordCodes) ? tsneCoordCodes : [tsneCoordCodes]; // Ensure 'codes' is always an array
        // Filter out conflicted codes only if in 'thisSemester' tab AND showConflicts is true
        const filteredCodes = currentTsneCodes.filter(code =>
          code && (activeTab === 'yourHistory' || !showConflicts || !conflicted.includes(code))
        );

        if (filteredCodes.length === 0) return;

        const key = `${x},${y}`;
        if (!mergedNodes.has(key)) {
          // For new merged node, collect all course details at this coordinate
          const coursesAtPoint = [];
          filteredCodes.forEach(code => {
            const courseDetail = courseDetails.find(cd => cd.course_codes.includes(code));
            if (courseDetail) {
              coursesAtPoint.push({ code, semester: coordSemester }); // Add code and its specific semester
            }
          });

          const firstCode = filteredCodes[0] || "TBD";
          const dept = firstCode.split('-')[0];

          mergedNodes.set(key, {
            id: firstCode,
            x,
            y,
            department: dept,
            codes: filteredCodes, // Still keep for general checks
            coursesAtPoint: coursesAtPoint, // New: list of {code, semester} for this point
            shape: getShapeForDept(dept),
            color: majorColorMap.get(dept) || "#999",
            // Separate highlighting for history vs search
            highlighted: filteredCodes.some(code => highlighted.includes(code)),
            historyHighlighted: coursesAtPoint.some(ca =>
              userCourseCodes.some(uc => uc.code === ca.code && uc.semester === ca.semester)
            ),
            conflicted: false, // We already filtered conflicted codes
            semester: coordSemester, // This node represents a coordinate within this semester
            isReference: !!isReference, // Historical / non-enrollable context node
          });
        } else {
          const node = mergedNodes.get(key);
          node.codes = [...new Set([...node.codes, ...filteredCodes])];
          const departments = node.codes.map(code => code.split('-')[0]);
          node.department = departments[0];
          node.shape = getShapeForDept(node.department);
          node.color = majorColorMap.get(node.department) || "#999";

          // Add new courses (with semester) to coursesAtPoint for existing node
          filteredCodes.forEach(code => {
            const courseDetail = courseDetails.find(cd => cd.course_codes.includes(code));
            if (courseDetail) {
              const existingCourse = node.coursesAtPoint.find(ca => ca.code === code && ca.semester === coordSemester);
              if (!existingCourse) {
                node.coursesAtPoint.push({ code, semester: coordSemester });
              }
            }
          });

          // Re-evaluate highlighting for existing node
          node.highlighted = filteredCodes.some(code => highlighted.includes(code));
          node.historyHighlighted = node.coursesAtPoint.some(ca =>
            userCourseCodes.some(uc => uc.code === ca.code && uc.semester === ca.semester)
          );
          node.conflicted = false; // We already filtered conflicted codes
        }
      });

      const finalNodes = Array.from(mergedNodes.values());

      // --- Z-ORDER: Render highlighted nodes last (on top) ---
      let nodesToRender = finalNodes;
      if (activeTab === 'yourHistory') {
        const highlightedNodes = finalNodes.filter(d => d.historyHighlighted);
        const otherNodes = finalNodes.filter(d => !d.historyHighlighted);
        nodesToRender = [...otherNodes, ...highlightedNodes];
      } else {
        const highlightedNodes = finalNodes.filter(d => d.highlighted);
        const otherNodes = finalNodes.filter(d => !d.highlighted);
        nodesToRender = [...otherNodes, ...highlightedNodes];
      }

      const width = dimensions.width;
      const height = dimensions.height;
      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();
      svg
        .attr("width", width)
        .attr("height", height)
        .attr("viewBox", `0 0 ${width} ${height}`);

      // Label-reveal state (defined here, populated below).
      let currentTransform = d3.zoomIdentity;
      let labelPointer = null; // [x, y] in the inner-g coordinate space
      let refreshLabels = () => {};

      const zoom = d3.zoom()
        .scaleExtent([0.5, 8])
        .on("zoom", (event) => {
          g.attr("transform", event.transform);
          currentTransform = event.transform;
          refreshLabels();
        });

      svg.call(zoom);

      const getFontSize = (baseSize) => {
        // Scale font size based on width, with a minimum and maximum for readability
        const minWidth = 800; // Minimum width where baseSize is applied
        const maxWidth = 1920; // Maximum width where scaling stops
        const minFactor = 0.5; // Minimum scaling factor (was 0.8)
        const maxFactor = 1.0; // Maximum scaling factor (was 1.2)

        if (width <= minWidth) return `${baseSize * minFactor}px`;
        if (width >= maxWidth) return `${baseSize * maxFactor}px`;

        // Linear interpolation between min and max factor
        const factor = minFactor + (maxFactor - minFactor) * ((width - minWidth) / (maxWidth - minWidth));
        return `${baseSize * factor}px`;
      };

      const g = svg.append("g");

      g.append("rect")
        .attr("width", width)
        .attr("height", height)
        .attr("fill", "#faf8f4");

      /*"""Add a reset zoom button
      const resetButton = svg.append("g")
        .attr("class", "reset-zoom")
        .attr("transform", `translate(${width}, 60)`)
        .style("cursor", "pointer");

      resetButton.append("rect")
        .attr("width", 80)
        .attr("height", 30)
        .attr("rx", 5)
        .attr("fill", "rgba(249, 247, 251, 0.95)")
        .attr("stroke", "#e8e2f1")
        .attr("stroke-width", 1);

      resetButton.append("text")
        .attr("x", 40)
        .attr("y", 20)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .style("font-size", getFontSize(12))
        .text("Reset View");

      resetButton.on("click", () => {
        svg.transition()
          .duration(750)
          .call(zoom.transform, d3.zoomIdentity);
      }); */



      // Legend is now a floating toggle overlay (not a reserved sidebar), so the
      // map uses the full width. 0 = no left gutter.
      const leftLegendWidth = 0;

      // Right legend dimensions (there is nothing now so set to 0)
      const rightLegendWidth = 0//leftLegendWidth; // Reduced width

      // Create padding for chart area
      const topPadding = 52; // Title space
      //console.log('topPadding:', topPadding)
      const bottomPadding = 200 - width * 0.07; // Footer space
      const leftPadding = leftLegendWidth;
      const rightPadding = rightLegendWidth;

      const xExtent = d3.extent(finalNodes, (d) => d.x);
      const yExtent = d3.extent(finalNodes, (d) => d.y);

      // Add padding to spread out coordinates more (decreased from 0.05 to 0.001 for more spacing)
      // This makes the domain wider, so coordinates are more spread out on the screen
      const xMargin = (xExtent[1] - xExtent[0]) * 0.01;
      const yMargin = (yExtent[1] - yExtent[0]) * 0.01;

      const xScale = d3
        .scaleLinear()
        .domain([xExtent[0] - xMargin, xExtent[1] + xMargin])
        .range([leftPadding, width - rightPadding]);

      const yScale = d3
        .scaleLinear()
        .domain([yExtent[0] - yMargin, yExtent[1] + yMargin])
        .range([topPadding, height - bottomPadding]);

      // ---- Relax overlapping nodes ----------------------------------------
      // Many t-SNE points are near-coincident (e.g. the music blob), so dots
      // stack. A gentle collision force — anchored to each course's TRUE
      // position — nudges them apart while preserving the overall layout.
      // Relaxed pixel positions are stored on d.px / d.py and used everywhere.
      {
        const sim = nodesToRender.map((d) => ({
          d, ax: xScale(d.x), ay: yScale(d.y), x: xScale(d.x), y: yScale(d.y),
        }));
        const f = d3.forceSimulation(sim)
          .force("x", d3.forceX((s) => s.ax).strength(0.5))
          .force("y", d3.forceY((s) => s.ay).strength(0.5))
          .force("collide", d3.forceCollide(7.5).strength(0.85).iterations(2))
          .stop();
        for (let i = 0; i < 140; i++) f.tick();
        sim.forEach((s) => { s.d.px = s.x; s.d.py = s.y; });
      }

      // ---- Division "territory" regions: soft tints + large serif labels ----
      // Computed from where each division's courses ACTUALLY fall (real t-SNE
      // positions) — not invented clusters. Gives the map cartographic
      // orientation without per-node label clutter.
      const REGION_TINT = {
        "Arts": "#f0d7cc",
        "Humanities": "#cfe6ec",
        "Sciences": "#d8e8d3",
        "Social Sciences": "#dcd8f1",
      };
      const regionDefs = svg.append("defs");
      const regionGroups = {};
      nodesToRender.forEach((n) => {
        const tr = getTrancheForDept(n.department);
        if (!REGION_TINT[tr]) return;
        (regionGroups[tr] = regionGroups[tr] || []).push([n.px, n.py]);
      });
      const REGION_INK = {
        "Arts": "#b5694a",
        "Humanities": "#3f7d92",
        "Sciences": "#5a8a52",
        "Social Sciences": "#6a5aa0",
      };
      const regionLayer = g.append("g").style("pointer-events", "none");
      // Pass 1: geometry for each division.
      const regionGeo = Object.entries(regionGroups)
        .filter(([, pts]) => pts.length >= 3)
        .map(([tr, pts]) => {
          const cx = d3.mean(pts, (p) => p[0]);
          const cy = d3.mean(pts, (p) => p[1]);
          const rx = Math.sqrt(d3.mean(pts, (p) => (p[0] - cx) ** 2)) * 2.1 + 70;
          const ry = Math.sqrt(d3.mean(pts, (p) => (p[1] - cy) ** 2)) * 2.1 + 70;
          return { tr, cx, cy, rx, ry };
        });
      const mapMidY = d3.mean(regionGeo, (r) => r.cy);
      // Pass 2: soft tints.
      regionGeo.forEach(({ tr, cx, cy, rx, ry }) => {
        const gid = `region-${tr.replace(/\s+/g, "")}`;
        const grad = regionDefs.append("radialGradient")
          .attr("id", gid).attr("cx", "50%").attr("cy", "50%").attr("r", "60%");
        grad.append("stop").attr("offset", "0%")
          .attr("stop-color", REGION_TINT[tr]).attr("stop-opacity", 0.5);
        grad.append("stop").attr("offset", "100%")
          .attr("stop-color", REGION_TINT[tr]).attr("stop-opacity", 0);
        regionLayer.append("ellipse")
          .attr("cx", cx).attr("cy", cy).attr("rx", rx).attr("ry", ry)
          .attr("fill", `url(#${gid})`);
      });
      // Pass 3: serif region labels — pushed outward (above clusters in the top
      // half, below in the bottom half) and letter-spaced so they read as calm
      // cartographic titles instead of colliding in the middle.
      regionGeo.forEach(({ tr, cx, cy, rx, ry }) => {
        const above = cy <= mapMidY;
        const ly = cy + (above ? -1 : 1) * (ry * 0.6);
        const fontSize = Math.max(28, Math.min(rx * 0.3, 54));
        const clampedX = Math.max(rx * 0.5 + 20, Math.min(cx, width - rx * 0.5 - 20));
        regionLayer.append("text")
          .attr("x", clampedX).attr("y", ly).attr("text-anchor", "middle")
          .text(tr.toUpperCase())
          .attr("fill", REGION_INK[tr]).attr("opacity", 0.4)
          .style("font-family", "Gelasio, Georgia, serif")
          .style("font-weight", 600)
          .style("letter-spacing", `${Math.round(fontSize * 0.28)}px`)
          .style("paint-order", "stroke")
          .style("text-indent", `${Math.round(fontSize * 0.28)}px`);
      });

      // Use nodesToRender instead of finalNodes
      const nodeGroup = g
        .append("g")
        .selectAll("g")
        .data(nodesToRender)
        .enter()
        .append("g")
        .attr("transform", (d) => `translate(${d.px},${d.py})`);

      nodeGroup.each(function (d) {
        const group = d3.select(this);
        // Check if this node contains any of the user's courses in the correct semester
        // Now using d.coursesAtPoint for accurate check

        // Calculate highlighted codes count first (needed for both tabs)
        const highlightedCodes = d.codes.filter(code =>
          highlighted.some(highlightedCode => {
            // If the highlighted code contains a slash, split it and check each part
            if (highlightedCode.includes('/')) {
              return highlightedCode.split('/').includes(code);
            }
            return highlightedCode === code;
          })
        );
        const highlightedCount = highlightedCodes.length;

        // Adjust size and opacity based on whether it's a user's course and which tab we're in
        let baseSize, opacity;
        if (activeTab === 'yourHistory') {
          if (d.highlighted) { // If course is highlighted by search, give it the largest size
            baseSize = 15;
          } else if (d.historyHighlighted) { // If it's a historical user course (and not also highlighted by search), give it a medium size
            baseSize = 14;
          } else { // Default size for other courses
            baseSize = 7;
          }
          // Opacity: full for search-highlighted or historical courses, much lower for others in history tab
          opacity = (d.highlighted || d.historyHighlighted) ? 1 : 0.15;
        } else {
          // Single Semester View: Adjust base sizes for single vs multi-code courses
          if (d.codes.length === 1) {
            // Single code courses: slightly larger base size
            baseSize = highlightedCount > 0 ? 15 : 6;
          } else {
            // Multi-code courses: slightly smaller base size
            baseSize = highlightedCount > 0 ? 15 : 6;
          }
          opacity = 1;
        }

        // Historical (non-current-semester) reference nodes: dim + smaller so
        // they read as background context, not enrollable courses. Still
        // rendered so prereq/unlock lineage can reach them on hover.
        if (d.isReference) {
          opacity = 0.28;
          baseSize = Math.max(3, Math.round(baseSize * 0.55));
        }

        // Get all departments for this course
        const departments = d.codes.map(code => code.split('-')[0]);
        const uniqueDepts = [...new Set(departments)];

        // ---- Uniform circle rendering ----------------------------------
        // Shapes removed (they added noise + the ugly cross-list pie). Every
        // course is a dot colored by its PRIMARY department; cross-listed
        // courses get a thin ring in their second field's color, so "sits
        // between two fields" reads at a glance. Full code list is in the popup.
        {
          const isMuted = activeTab === 'yourHistory' && !d.historyHighlighted && !d.highlighted;
          const boost = (activeTab === 'yourHistory' && d.historyHighlighted) ||
                        (activeTab !== 'yourHistory' && highlightedCount > 0);
          // Display color for a department (muted in history bg, boosted when selected).
          const shade = (dp) => {
            if (isMuted) return "#d6d3ce";
            let c = majorColorMap.get(dp) || "#999";
            if (boost) {
              const hsl = d3.hsl(d3.color(c));
              hsl.s = Math.min(1, hsl.s * 1.3);
              hsl.l = Math.min(0.64, hsl.l * 1.08);
              c = hsl.toString();
            }
            return c;
          };

          const r = baseSize;
          if (uniqueDepts.length <= 1) {
            // Single department → solid dot.
            group.append("circle")
              .attr("class", "node-fill")
              .attr("r", r)
              .attr("fill", shade(departments[0]))
              .attr("fill-opacity", opacity);
          } else {
            // Cross-listed → dot split into equal pie wedges, one per department,
            // so the course is literally "part" of each field it belongs to.
            const ang = (2 * Math.PI) / uniqueDepts.length;
            uniqueDepts.forEach((dp, i) => {
              const arc = d3.arc()
                .innerRadius(0).outerRadius(r)
                .startAngle(i * ang).endAngle((i + 1) * ang);
              group.append("path")
                .attr("class", "node-fill")
                .attr("d", arc())
                .attr("fill", shade(dp))
                .attr("fill-opacity", opacity);
            });
          }
        }


        // Store scaled position for hover effects
        const nodeX = d.px;
        const nodeY = d.py;

        group.style("cursor", "pointer")
          .on("click", () => {
            const full = courseDetails.find((entry) =>
              entry.course_codes.some(code => d.codes.includes(code))
            );
            setSelectedCourse(full || { course_title: "Unknown", ...d });
          })
          .on("mouseover", function (event) {
            // Proposal 4: Glow + scale effect
            d3.select(this)
              .raise() // bring to front
              .transition().duration(150)
              .attr("transform", `translate(${nodeX},${nodeY}) scale(1.3)`)
              .style("filter", `drop-shadow(0 0 6px ${d.color})`);
            // Proposal 3: Show tooltip
            const full = courseDetails.find((entry) =>
              entry.course_codes.some(code => d.codes.includes(code))
            );
            const rect = mapContainerRef.current?.getBoundingClientRect();
            if (rect) {
              // N = number of AND-groups the student must satisfy.
              // Dashed vs solid lines convey OR-alternatives within a group.
              const groups = full?.prereq_groups || [];
              const groupCount = groups.length;
              const prereqSummary = groupCount > 0
                ? `${groupCount} prereq${groupCount === 1 ? '' : 's'}`
                : null;
              const permissionChip = full?.instructor_permission_ok
                ? (groupCount > 0 ? "or w/ permission" : "w/ permission")
                : null;
              const classYearChip = (full?.class_year_restriction && full.class_year_restriction.length > 0)
                ? `${full.class_year_restriction.join('/')} only`
                : null;

              setTooltip({
                x: event.clientX - rect.left + 12,
                y: event.clientY - rect.top - 10,
                code: d.codes.join(' / '),
                title: full?.course_title || 'Unknown Course',
                prereqSummary,
                permissionChip,
                classYearChip,
                unlockCount: full?.unlocks?.length || 0,
              });
            }
            // Proposal 7: Show label on hover even if hidden
            d3.select(this).select(".node-label").style("opacity", 1);

            // Lineage: ALWAYS dim non-hovered nodes; color prereqs (warm) and
            // successors (cool) if any; draw connecting lines between them.
            // Codes in a multi-code (OR) group get dashed lines to signal
            // "one alternative"; codes alone in their group get solid lines.
            const groups = full?.prereq_groups || [];
            const prereqSet = new Set();
            const dashedCodes = new Set();
            groups.forEach((g) => {
              const codes = g?.codes || [];
              codes.forEach((c) => prereqSet.add(c));
              if (codes.length > 1) {
                codes.forEach((c) => dashedCodes.add(c));
              }
            });
            // Fallback for courses whose prereq_groups wasn't populated (e.g.,
            // one of the 2 rules the LLM couldn't parse): use the flat list.
            if (prereqSet.size === 0 && Array.isArray(full?.prereqs)) {
              full.prereqs.forEach((c) => prereqSet.add(c));
            }
            const unlockSet = new Set(full?.unlocks || []);

            nodeGroup.each(function (other) {
              const sel = d3.select(this);
              const isSelf = other === d;
              const isPrereq = other.codes.some((c) => prereqSet.has(c));
              const isUnlock = other.codes.some((c) => unlockSet.has(c));
              if (isSelf || isPrereq || isUnlock) {
                sel.style("opacity", 1);
                if (!isSelf) {
                  sel.selectAll("circle.node-fill")
                    .attr("stroke", isPrereq ? "#d4886a" : "#5b8a5b")
                    .attr("stroke-width", 2.4)
                    .attr("stroke-opacity", 1);
                }
              } else {
                sel.style("opacity", 0.12);
              }
            });

            // Connecting lines — appended on top, low opacity so nodes stay visible.
            g.select(".lineage-lines").remove();
            if (prereqSet.size > 0 || unlockSet.size > 0) {
              const linesGroup = g
                .append("g")
                .attr("class", "lineage-lines")
                .style("pointer-events", "none");
              nodeGroup.each(function (other) {
                if (other === d) return;
                const isPrereq = other.codes.some((c) => prereqSet.has(c));
                const isUnlock = other.codes.some((c) => unlockSet.has(c));
                if (isPrereq || isUnlock) {
                  // Dashed line when this prereq is an OR-alternative in a
                  // multi-code group — signals "one option, see popup for the rest".
                  const isDashed = isPrereq && other.codes.some((c) => dashedCodes.has(c));
                  const line = linesGroup.append("line")
                    .attr("x1", d.px).attr("y1", d.py)
                    .attr("x2", other.px).attr("y2", other.py)
                    .attr("stroke", isPrereq ? "#d4886a" : "#5b8a5b")
                    .attr("stroke-width", 1.8)
                    .attr("stroke-opacity", 0.75)
                    .attr("stroke-linecap", "round");
                  if (isDashed) line.attr("stroke-dasharray", "5,4");
                }
              });
              // Make sure the hovered node stays on top of the lines
              d3.select(this).raise();
            }
          })
          .on("mousemove", function (event) {
            const rect = mapContainerRef.current?.getBoundingClientRect();
            if (rect) {
              setTooltip(prev => prev ? {
                ...prev,
                x: event.clientX - rect.left + 12,
                y: event.clientY - rect.top - 10,
              } : null);
            }
          })
          .on("mouseout", function () {
            d3.select(this)
              .transition().duration(150)
              .attr("transform", `translate(${nodeX},${nodeY}) scale(1)`)
              .style("filter", "none");
            setTooltip(null);

            // Restore lineage: remove lines, clear dim, restore default stroke.
            g.select(".lineage-lines").remove();
            nodeGroup.style("opacity", null);
            nodeGroup.each(function (other) {
              d3.select(this).selectAll("circle.node-fill")
                .attr("stroke", d3.color(other.color)?.darker(0.5) || "#666")
                .attr("stroke-width", 0.5)
                .attr("stroke-opacity", 0.3);
            });
          });

        // Proposal 4: subtle stroke on the fill dot for a clickable feel
        // (exclude the cross-list ring so its color isn't overwritten).
        group.selectAll("circle.node-fill")
          .attr("stroke", d3.color(d.color)?.darker(0.5) || "#666")
          .attr("stroke-width", 0.5)
          .attr("stroke-opacity", 0.3);
      });

      // ===== Labels: department anchors + collision-culled course codes =====
      // Two tiers (like a real map):
      //  • Dept anchors — one faint code per department at its cluster centroid,
      //    so you always know what AREA you're looking at. Fade out as you zoom
      //    in (course labels take over).
      //  • Course labels — per-course codes, CONSTANT screen size, greedily
      //    placed so they never overlap. Pinned (selected/search/history) +
      //    the courses nearest the cursor are always attempted first; the rest
      //    fill whatever space is free, so more appear as you zoom in.
      // Both live in an overlay drawn in screen space (not zoomed), recomputed
      // on every zoom / pan / cursor move.
      const codeOf = (d) => (d.codes.length > 1 ? `${d.codes[0]}…` : d.codes[0]);
      // Skip labels for historical reference nodes — they're background context.
      const labelData = nodesToRender
        .filter((d) => !d.isReference)
        .map((d) => ({
          code: codeOf(d),
          px: d.px,
          py: d.py,
          pinned: !!(d.highlighted || d.historyHighlighted),
        }));

      // Department centroids (relaxed positions).
      const deptPts = {};
      nodesToRender.forEach((n) => {
        const dep = n.department;
        if (!dep || dep === "FYSE") return;
        (deptPts[dep] = deptPts[dep] || []).push([n.px, n.py]);
      });
      const deptAnchors = Object.entries(deptPts)
        .filter(([, pts]) => pts.length >= 2)
        .map(([dep, pts]) => ({
          dep,
          px: d3.mean(pts, (p) => p[0]),
          py: d3.mean(pts, (p) => p[1]),
        }));

      const overlay = svg.append("g").attr("class", "label-overlay").style("pointer-events", "none");
      const anchorSel = overlay.selectAll("text.dept-anchor").data(deptAnchors).enter()
        .append("text")
        .attr("class", "dept-anchor")
        .attr("text-anchor", "middle")
        .text((a) => a.dep)
        .style("font-family", "Bitter, ui-monospace, monospace")
        .style("font-weight", 600)
        .style("font-size", "11px")
        .style("letter-spacing", "0.06em")
        .style("fill", "#6f6a7a");
      const courseSel = overlay.selectAll("g.course-label").data(labelData).enter()
        .append("g").attr("class", "course-label");
      courseSel.append("text")
        .attr("text-anchor", "middle")
        .style("font-family", "Bitter, ui-monospace, monospace")
        .style("font-weight", 700)
        .style("font-size", "11px")
        .style("fill", "#2a2638")
        .text((d) => d.code);

      const PROX_RADIUS = 110; // px around cursor that prioritizes labels
      const boxesOverlap = (a, b) =>
        Math.abs(a.x - b.x) * 2 < a.w + b.w && Math.abs(a.y - b.y) * 2 < a.h + b.h;

      refreshLabels = () => {
        const t = currentTransform;
        const k = t.k;
        const toScreen = (px, py) => [px * k + t.x, py * k + t.y];
        const ptr = labelPointer ? toScreen(labelPointer[0], labelPointer[1]) : null;

        // Dept anchors: HIDDEN by default — we don't want to predispose
        // students to "this region = X dept" when the whole point is
        // interdisciplinary discovery. Only reveal the anchor nearest the
        // cursor, on hover, so a student who's curious can still ask "what
        // area am I in?" without it being broadcast across the map.
        const ANCHOR_HOVER_R = 130;
        anchorSel
          .attr("x", (a) => toScreen(a.px, a.py)[0])
          .attr("y", (a) => toScreen(a.px, a.py)[1])
          .style("opacity", (a) => {
            if (!ptr) return 0;
            const [sx, sy] = toScreen(a.px, a.py);
            const dist = Math.hypot(sx - ptr[0], sy - ptr[1]);
            if (dist > ANCHOR_HOVER_R) return 0;
            return 0.55 * (1 - dist / ANCHOR_HOVER_R);
          });

        // Course labels: greedy non-overlap placement, prioritised.
        const placed = [];
        const ranked = labelData
          .map((d, i) => {
            const [sx, sy] = toScreen(d.px, d.py);
            let pr = d.pinned ? 0 : 5;
            let near = false;
            if (ptr) {
              const dist = Math.hypot(sx - ptr[0], sy - ptr[1]);
              if (dist < PROX_RADIUS) { pr = d.pinned ? 0 : 1; near = true; }
            }
            return { d, i, sx, sy, pr, near };
          })
          // Only consider non-pinned once there's room (zoomed in) or near cursor.
          .filter((r) => r.d.pinned || r.near || k >= 1.5)
          .sort((a, b) => a.pr - b.pr || a.sx - b.sx);

        const show = new Set();
        ranked.forEach((r) => {
          const w = r.d.code.length * 6.5 + 6;
          const box = { x: r.sx, y: r.sy - (7 + 8), w, h: 14 };
          if (placed.some((p) => boxesOverlap(box, p))) return;
          placed.push(box);
          show.add(r.i);
        });

        courseSel
          .attr("transform", (d) => {
            const [sx, sy] = toScreen(d.px, d.py);
            return `translate(${sx},${sy - 14})`;
          })
          .style("opacity", (d, i) => (show.has(i) ? 1 : 0));
      };

      // Cursor sweep prioritises nearby labels (namespaced; zoom-drag untouched).
      svg.on("mousemove.labels", (event) => {
        labelPointer = d3.pointer(event, g.node());
        refreshLabels();
      });
      svg.on("mouseleave.labels", () => { labelPointer = null; refreshLabels(); });
      refreshLabels();

      // Preview/eval hook: ?z=<scale> starts the map zoomed into the center.
      const zParam = parseFloat(new URLSearchParams(window.location.search).get("z"));
      if (zParam && zParam > 1) {
        svg.call(
          zoom.transform,
          d3.zoomIdentity
            .translate(width / 2, height / 2)
            .scale(zParam)
            .translate(-width / 2, -height / 2)
        );
      }

      // Proposal 5: Pulse animation on highlighted nodes
      nodeGroup.filter(d => d.highlighted || d.historyHighlighted)
        .append("circle")
        .attr("class", "pulse-ring")
        .attr("r", 12)
        .attr("fill", "none")
        .attr("stroke", d => majorColorMap.get(d.department) || "#999")
        .attr("stroke-width", 2)
        .attr("stroke-opacity", 0.6)
        .each(function pulse() {
          d3.select(this)
            .attr("r", 12).attr("stroke-opacity", 0.5)
            .transition().duration(1200).ease(d3.easeQuadOut)
            .attr("r", 24).attr("stroke-opacity", 0)
            .transition().duration(0)
            .on("end", pulse);
        });

      if (!legendCollapsed) {
        // === Floating legend card (overlay, not a reserved sidebar) ===
        const legendItemHeight = 18;
        const legendColWidth = 85;
        const legendCardW = 218;
        const legendCardX = 12;
        const legendCardY = 60; // below the floating tab pills
        const legendPaddingX = legendCardX + 8;
        const legendPaddingY = legendCardY + 18;

        // Estimate card height from the content so dots can't sneak under it.
        let legendCardH = 16;
        Object.values(TRANCHES).forEach((depts) => {
          legendCardH += 22 + Math.ceil(depts.length / 2) * legendItemHeight + 8;
        });
        legendCardH += 30; // disclaimer line

        // Legend background: opaque off-white card with border.
        svg.append("rect")
          .attr("x", legendCardX)
          .attr("y", legendCardY)
          .attr("width", legendCardW)
          .attr("height", legendCardH)
          .attr("fill", "#fbfaf7")
          .attr("rx", 12)
          .attr("stroke", "#d2cfcd")
          .attr("stroke-width", 1);

        const legend = svg
          .append("g")
          .attr("transform", `translate(${legendPaddingX}, ${legendPaddingY})`)
          .attr("class", "legend");

        // Helper to draw a legend shape icon
        const drawLegendShape = (parent, shapeType, color, size) => {
          switch (shapeType) {
            case "circle":
              parent.append("circle").attr("r", size).attr("fill", color);
              break;
            case "doubleCircle":
              parent.append("circle").attr("r", size * 1.2).attr("fill", "none")
                .attr("stroke", color).attr("stroke-width", 1.5);
              parent.append("circle").attr("r", size * 0.8).attr("fill", color);
              break;
            case "square":
              parent.append("rect").attr("x", -size).attr("y", -size)
                .attr("width", size * 2).attr("height", size * 2).attr("fill", color);
              break;
            case "triangle":
              parent.append("path")
                .attr("d", d3.symbol().type(d3.symbolTriangle).size(size * size * 4)())
                .attr("fill", color);
              break;
            case "star":
              parent.append("path")
                .attr("d", d3.symbol().type(d3.symbolStar).size(size * size * 4)())
                .attr("fill", color);
              break;
          }
        };

        let currentY = 0;
        const trancheEntries = Object.entries(TRANCHES);

        trancheEntries.forEach(([trancheName, depts], trancheIdx) => {
          const shapeType = TRANCHE_SHAPES[trancheName] || "circle";

          // Tranche group header: shape icon + tranche name
          const headerGroup = legend.append("g")
            .attr("transform", `translate(0, ${currentY})`);

          // Division header: a brand-purple dot + name (icons are dots now to
          // match the map, which no longer uses per-division shapes).
          const headerIcon = headerGroup.append("g")
            .attr("transform", "translate(8, 0)");
          drawLegendShape(headerIcon, "circle", "#3f1f69", 6);

          headerGroup.append("text")
            .attr("x", 20)
            .attr("y", 5)
            .text(trancheName)
            .style("font-weight", "bold")
            .style("font-size", getFontSize(12))
            .style("fill", "#3f1f69");

          currentY += 22;

          // Departments in 2 columns under this tranche header
          const deptsPerCol = Math.ceil(depts.length / 2);
          depts.forEach((dept, i) => {
            const col = Math.floor(i / deptsPerCol);
            const row = i % deptsPerCol;
            const color = majorColorMap.get(dept) || "#999";

            const item = legend.append("g")
              .attr("transform", `translate(${12 + col * legendColWidth}, ${currentY + row * legendItemHeight})`);

            // Department dot (matches the map nodes)
            const iconGroup = item.append("g")
              .attr("transform", "translate(0, 0)");
            drawLegendShape(iconGroup, "circle", color, 4);

            item.append("text")
              .attr("x", 8)
              .attr("y", 4)
              .text(dept === "SWAG" ? "SWAG/WAGS" : dept)
              .style("font-size", getFontSize(11))
              .style("fill", "#444");
          });

          currentY += deptsPerCol * legendItemHeight + 6;

          if (trancheIdx < trancheEntries.length - 1) {
            currentY += 4;
          }
        });

        // Disclaimer text at the bottom of legend
        currentY += 10;
        const disclaimerText = legend.append("text")
          .attr("x", 0)
          .attr("y", currentY)
          .style("font-size", getFontSize(10))
          .style("fill", "#999");
        disclaimerText.append("tspan").text("Special topics and thesis");
        disclaimerText.append("tspan")
          .attr("x", 0).attr("dy", "1.3em")
          .text("courses are not displayed.");

        // Visual separator between legend and graph
        svg.append("rect")
          .attr("x", leftPadding - 20)
          .attr("y", 0)
          .attr("width", 1.5)
          .attr("height", height)
          .attr("fill", "#e8e2f1");
      }

      // Main title with adjusted positioning
      svg
        .append("text")
        .attr("x", leftPadding + (width - leftPadding - rightPadding) / 2)
        .attr("y", 290 - width * 0.15)
        .attr("text-anchor", "middle")
        .style("font-size", getFontSize(18))
        .style("font-weight", "bold")
        .text(
          activeTab === 'thisSemester'
            ? `Course Similarity Graph - ${selectedSemester}`
            : 'My Amherst Curriculum'
        );

      // Add CSS to handle label scaling and pulse rings
      const style = document.createElement('style');
      style.textContent = `
        .node-label {
          pointer-events: none;
          text-rendering: geometricPrecision;
          transition: opacity 0.2s ease;
        }
        .pulse-ring {
          pointer-events: none;
        }
      `;
      document.head.appendChild(style);

      setLoading(false);
    } catch (err) {
      console.error('Error initializing graph:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [
    dimensions,
    mode,
    svgReady,
    highlighted,
    conflicted,
    graphData,
    courseDetailsData,
    tsneCoords,
    selectedSemester,
    activeTab,
    showConflicts,
    legendCollapsed,
  ]);

  const zoomRef = useRef(null);

  useEffect(() => {
    const zoomBehavior = d3.zoom()
      .scaleExtent([0.5, 5])
      .on("zoom", (event) => {
        const svgEl = d3.select(svgRef.current);
        svgEl.select("g").attr("transform", event.transform);
      });

    d3.select(svgRef.current).call(zoomBehavior);

    zoomRef.current = zoomBehavior;
  }, []);

  // Function to handle image download
  const handleDownloadImage = async () => {
    const mapContainer = contentToCaptureRef.current; // Use the new ref to get the actual DOM element for capture
    if (mapContainer) {
      try {
        // Temporarily adjust scroll to top if needed, though html2canvas handles scrolling usually
        const originalScrollY = window.scrollY;
        window.scrollTo(0, 0);

        const canvas = await html2canvas(mapContainer, {
          useCORS: true, // If images/elements are loaded from different origins
          scale: 8, // Increase scale for higher resolution
          scrollY: -window.scrollY, // Correct scrolling issue
          width: mapContainer.offsetWidth, // Capture the element's actual width
          height: mapContainer.offsetHeight, // Capture the element's actual height
        });

        const image = await domtoimage.toJpeg(mapContainer, { quality: 1, bgcolor: "#fff" });
        const link = document.createElement('a');
        link.href = image;
        link.download = 'my_open_curriculum.jpeg';
        link.click();
        link.remove(); // safer than removeChild

        // Restore original scroll position
        window.scrollTo(0, originalScrollY);

      } catch (err) {
        console.error('Error downloading map as image:', err);
        alert('Failed to download image.');
      }
    } else {
      alert('Map container not found.');
    }
  };

  // Add a function to get the title based on the active tab
  const getTitle = () => {
    if (activeTab === 'thisSemester') {
      return `Course Similarity Graph - ${selectedSemester}`;
    } else {
      return 'Your Course History';
    }
  };

  // Function to handle onboarding popup close
  const handleOnboardingClose = () => {
    setShowOnboarding(false);
    localStorage.setItem('hasSeenOnboarding', 'true');
  };

  // Function to handle adding a course to selected courses
  const handleAddSelectedCourse = async (course) => {
    if (!course.course_codes) return;

    const codes = Array.isArray(course.course_codes)
      ? course.course_codes
      : [course.course_codes];

    // If there are multiple codes, combine them with a slash
    const codeToAdd = codes.length > 1 ? codes.join('/') : codes[0];

    // Check if the course is already selected
    const isSelected = highlighted.some(code => {
      if (code.includes('/')) {
        return code === codeToAdd;
      }
      return codes.includes(code);
    });

    // Update highlighted courses and get the new state
    const newHighlighted = isSelected
      ? highlighted.filter(code => {
        if (code.includes('/')) {
          return code !== codeToAdd;
        }
        return !codes.includes(code);
      })
      : [...highlighted, codeToAdd];

    // Update highlighted courses
    onHighlight(newHighlighted);

    // Keep userCourseCodes in sync with the optimistic add/remove so the
    // tab-switch auto-populate effect doesn't wipe our highlight when the
    // user goes Single semester -> My course history -> back. Without this,
    // the effect re-reads stale userCourseCodes and calls onHighlight([]).
    setUserCourseCodes((prev) => {
      const match = (uc) =>
        codes.includes(uc.code) && uc.semester === selectedSemester;
      return isSelected
        ? prev.filter((uc) => !match(uc))
        : [...prev, { code: codeToAdd, semester: selectedSemester }];
    });

    // Skip conflict checking in public mode
    if (isPublicMode) {
      return;
    }

    // Check for conflicts with the new selection
    const response = await fetch(`${backendUrl}/conflicted_courses`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        taken_courses: newHighlighted, // Use the new state directly
        semester: selectedSemester
      }),
    });

    const data = await response.json();
    onConflicted(data.conflicted_courses);
  };

  return (
    <div
      ref={mapContainerRef}
      className="relative h-full w-full overflow-hidden bg-canvas"
    >
      {/* Floating controls (no full-width bar — the graph is the page) */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-start justify-between p-3">
        {/* View tabs as a pill group */}
        <div className="pointer-events-auto inline-flex rounded-full border border-line bg-surface/90 p-1 shadow-card backdrop-blur">
          <button
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${activeTab === 'thisSemester' ? 'bg-primary text-white' : 'text-ink-muted hover:text-ink'}`}
            onClick={() => setActiveTab('thisSemester')}
          >
            Single semester
          </button>
          {!isPublicMode && (
            <button
              className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${activeTab === 'yourHistory' ? 'bg-primary text-white' : 'text-ink-muted hover:text-ink'}`}
              onClick={() => setActiveTab('yourHistory')}
            >
              My course history
            </button>
          )}
        </div>

        <div className="pointer-events-auto flex items-center gap-2">
          {activeTab === 'yourHistory' && !isPublicMode && (
            <button
              className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface/90 px-3 py-1.5 text-sm font-medium text-ink-muted shadow-card backdrop-blur transition-colors hover:text-ink"
              onClick={handleDownloadImage}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Download
            </button>
          )}

          {activeTab === 'thisSemester' && (
            <label className="conflicts-toggle pointer-events-auto flex cursor-pointer items-center gap-2 rounded-full border border-line bg-surface/90 px-3 py-1.5 shadow-card backdrop-blur">
              <span className="text-sm font-medium text-ink-muted">Eliminate conflicts</span>
              <button
                type="button"
                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:shadow-focus ${showConflicts ? 'bg-primary' : 'bg-line-strong'}`}
                onClick={() => setShowConflicts(!showConflicts)}
              >
                <span className="sr-only">Eliminate Conflicts</span>
                <span
                  className={`pointer-events-none relative inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${showConflicts ? 'translate-x-4' : 'translate-x-0'}`}
                ></span>
              </button>
            </label>
          )}
        </div>
      </div>

      {activeTab === 'yourHistory' && !hasHistoryData() && !isPublicMode ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center" style={{ top: '48px' }}>
          <h3 className="font-display text-xl font-semibold text-ink">No course history yet</h3>
          <p className="mb-5 mt-1 max-w-sm text-sm text-ink-muted">
            Add the courses you've taken to see them mapped across your curriculum.
          </p>
          <button
            onClick={() => window.location.href = '/intake'}
            className="btn-primary"
          >
            Add courses
          </button>
        </div>
      ) : (
        <div className="relative w-full h-full">
          <div ref={contentToCaptureRef} className="relative w-full h-full bg-[#faf8f5]" style={{ top: '0px' }}>
            <svg
              ref={setSvgRef}
              className="map-svg w-full h-full absolute top-0 left-0"
              style={{ top: '0px' }}
            ></svg>

            {/* Proposal 3: Hover tooltip */}
            {tooltip && (
              <div
                style={{
                  position: 'absolute',
                  left: tooltip.x,
                  top: tooltip.y,
                  pointerEvents: 'none',
                  zIndex: 20,
                  transform: 'translateY(-100%)',
                }}
                className="max-w-[260px] rounded-lg border border-line bg-surface px-3 py-2 shadow-lift transition-opacity duration-150"
              >
                <div className="truncate text-xs font-bold text-primary">{tooltip.code}</div>
                <div className="mt-0.5 text-xs leading-tight text-ink-muted">{tooltip.title}</div>
                {(tooltip.prereqSummary || tooltip.unlockCount > 0 || tooltip.permissionChip || tooltip.classYearChip) && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1">
                    {tooltip.prereqSummary && (
                      <span
                        className="rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                        style={{ background: 'rgba(212,136,106,0.18)', color: '#a3603f' }}
                      >
                        {tooltip.prereqSummary}
                      </span>
                    )}
                    {tooltip.permissionChip && (
                      <span
                        className="rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                        style={{ background: 'rgba(212,164,74,0.22)', color: '#8a6320' }}
                      >
                        {tooltip.permissionChip}
                      </span>
                    )}
                    {tooltip.classYearChip && (
                      <span
                        className="rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                        style={{ background: 'rgba(127,99,184,0.18)', color: '#4d3a7a' }}
                      >
                        {tooltip.classYearChip}
                      </span>
                    )}
                    {tooltip.unlockCount > 0 && (
                      <span
                        className="rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                        style={{ background: 'rgba(91,138,91,0.18)', color: '#3d5e3d' }}
                      >
                        unlocks {tooltip.unlockCount}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Map controls: reset view + legend toggle */}
            <div className="absolute right-4 top-16 z-10 flex flex-col gap-2">
              <button
                className="rounded-lg border border-line bg-surface/95 px-3 py-1.5 text-xs font-medium text-ink shadow-sm backdrop-blur transition-colors hover:bg-surface-alt"
                onClick={() => {
                  if (svgRef.current) {
                    d3.select(svgRef.current)
                      .transition()
                      .duration(750)
                      .call(zoomRef.current.transform, d3.zoomIdentity);
                  }
                }}
              >
                Reset view
              </button>

              <button
                className="legend-toggle rounded-lg border border-line bg-surface/95 px-3 py-1.5 text-xs font-medium text-ink shadow-sm backdrop-blur transition-colors hover:bg-surface-alt"
                onClick={() => setLegendCollapsed(prev => !prev)}
              >
                {legendCollapsed ? "Show legend" : "Hide legend"}
              </button>
            </div>

            {loading && (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-canvas/70 backdrop-blur-sm">
                <div className="h-7 w-7 animate-spin rounded-full border-2 border-line-strong border-t-primary"></div>
                <div className="text-sm font-medium text-ink-muted">Loading the course map…</div>
              </div>
            )}

            {error && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-canvas/85 px-6 text-center">
                <div className="text-sm font-medium text-danger">Couldn't load the map: {error}</div>
              </div>
            )}

            <CoursePopup
              course={selectedCourse}
              onClose={() => setSelectedCourse(null)}
              onHighlight={onHighlight}
              highlighted={highlighted}
              activeTab={activeTab}
              onSelect={handleAddSelectedCourse}
              courseDetailsData={courseDetailsData}
              setSelectedCourse={setSelectedCourse}
            />

            {/* Semester Display - Only show in My Course History tab */}
            {/* {activeTab === 'yourHistory' && historyData && (
              <div className="absolute bottom-0 left-0 w-full bg-white/90 p-4 border-t border-[#e8e2f1]">
                <div className="flex flex-wrap gap-4">
                  {getSemestersWithData().map(semester => (
                    <div key={semester} className="flex-1 min-w-[200px]">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">{semester}</h3>
                      <div className="flex flex-wrap gap-2">
                        {historyData[semester].map(course => (
                          <span
                            key={course.code}
                            className="px-2 py-1 bg-[#faf8f5] text-sm text-gray-600 rounded"
                          >
                            {course.code}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )} */}

            {/* Bottom rail — compact term chip with prev/next, plus selected courses */}
            {activeTab === 'thisSemester' && (() => {
              // Code → human label: "2324F" → Fall 2023, "2324S" → Spring 2024, "2324J" → January 2024
              const formatSem = (s) => {
                if (!s || s.length < 5) return s || "";
                const yy = s.slice(0, 2), zz = s.slice(2, 4), t = s.slice(4);
                if (t === 'F') return `Fall 20${yy}`;
                if (t === 'S') return `Spring 20${zz}`;
                if (t === 'J') return `January 20${zz}`;
                return s;
              };
              const semIdx = AVAILABLE_SEMESTERS.indexOf(selectedSemester);
              const atStart = semIdx <= 0;
              const atEnd = semIdx >= AVAILABLE_SEMESTERS.length - 1;
              const goPrev = () => !atStart && setSelectedSemester(AVAILABLE_SEMESTERS[semIdx - 1]);
              const goNext = () => !atEnd && setSelectedSemester(AVAILABLE_SEMESTERS[semIdx + 1]);
              return (
                <div className="absolute bottom-0 left-0 w-full border-t border-line bg-surface/90 px-5 py-3 backdrop-blur-md">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-soft">Term</span>
                      {/* Only show prev/next arrows when there's more than one term to navigate */}
                      {AVAILABLE_SEMESTERS.length > 1 ? (
                        <div className="flex items-center gap-0.5 rounded-full border border-line bg-surface px-1 py-0.5 shadow-sm">
                          <button
                            onClick={goPrev}
                            disabled={atStart}
                            aria-label="Previous term"
                            className="flex h-6 w-6 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface-alt hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent"
                          >‹</button>
                          <span className="min-w-[120px] px-1 text-center font-display text-sm font-semibold text-primary">
                            {formatSem(selectedSemester)}
                          </span>
                          <button
                            onClick={goNext}
                            disabled={atEnd}
                            aria-label="Next term"
                            className="flex h-6 w-6 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface-alt hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent"
                          >›</button>
                        </div>
                      ) : (
                        <div className="rounded-full border border-line bg-surface px-4 py-1 font-display text-sm font-semibold text-primary shadow-sm">
                          {formatSem(selectedSemester)}
                        </div>
                      )}
                    </div>

                    <div className="flex min-w-0 items-center gap-2">
                      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-ink-soft">Selected</span>
                      <div className="flex max-w-[60vw] flex-wrap justify-end gap-1.5">
                        {highlighted.length > 0 ? (
                          highlighted.map((code) => (
                            <div
                              key={code}
                              className="group relative rounded-full border border-line bg-surface-alt px-2.5 py-0.5 text-sm text-primary transition-colors hover:bg-line"
                            >
                              {code}
                              <button
                                onClick={() => handleAddSelectedCourse({ course_codes: [code] })}
                                className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full border border-line bg-surface text-primary opacity-0 transition-opacity hover:bg-surface-alt group-hover:opacity-100"
                                aria-label={`Remove ${code}`}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                          ))
                        ) : (
                          <span className="text-sm italic text-ink-soft">None</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {showSettings && (
        <Settings onClose={() => setShowSettings(false)} />
      )}

    </div>
  );
}